import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Send } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  productImageDisplaySrc,
  resolveProductImageDisplayUrls,
} from "@/lib/product-image-display";
import { buildFabInteractiveFromProductCarousel } from "@/lib/whatsapp-interactive-message";
import {
  supportsCarousel,
  type WhatsAppProviderId,
} from "@/lib/whatsapp-provider-capabilities";

interface ProductImageRow {
  id: string;
  public_url: string;
  mime_type: string;
  sort_order: number;
  storage_provider?: string | null;
  object_key?: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  productId: string;
  productName: string;
  conversationId: string | null;
  channel: string;
  initialText: string;
  whatsappProvider?: WhatsAppProviderId;
  productUrl?: string | null;
  productPriceLabel?: string | null;
  onSent?: () => void;
}

export function ProductSendPreviewDialog({
  open,
  onOpenChange,
  workspaceId,
  productId,
  productName,
  conversationId,
  channel,
  initialText,
  whatsappProvider,
  productUrl,
  productPriceLabel,
  onSent,
}: Props) {
  const { toast } = useToast();
  const carouselAvailable = supportsCarousel(whatsappProvider);
  const [images, setImages] = useState<ProductImageRow[]>([]);
  const [displayUrls, setDisplayUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [caption, setCaption] = useState("");
  const [sendText, setSendText] = useState(true);
  const [sendAsCarousel, setSendAsCarousel] = useState(false);
  const [text, setText] = useState(initialText);

  const unsupportedChannel = channel !== "whatsapp";

  useEffect(() => {
    setText(initialText);
  }, [initialText]);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    setCaption("");
    setSendText(true);
    setSendAsCarousel(false);
    if (!productId || !workspaceId) return;
    setLoading(true);
    supabase
      .from("product_images" as any)
      .select("id, public_url, mime_type, sort_order, storage_provider, object_key")
      .eq("product_id", productId)
      .eq("workspace_id", workspaceId)
      .order("sort_order", { ascending: true })
      .limit(5)
      .then(async ({ data, error }) => {
        if (error) {
          console.error("preview load images:", error);
        }
        const rows = ((data as any) || []) as ProductImageRow[];
        setImages(rows);
        setSelected(new Set(rows.map((r) => r.id)));
        if (rows.length > 0) {
          const resolved = await resolveProductImageDisplayUrls(workspaceId, productId, rows.map((r) => r.id));
          setDisplayUrls(resolved);
        } else {
          setDisplayUrls({});
        }
        setLoading(false);
      });
  }, [open, productId, workspaceId]);

  useEffect(() => {
    if (!open || !carouselAvailable) return;
    const pickedCount = images.filter((i) => selected.has(i.id)).length;
    if (pickedCount >= 2) {
      setSendAsCarousel(true);
    }
  }, [open, carouselAvailable, images, selected]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSend = async () => {
    if (!conversationId) {
      toast({ title: "Conversa indisponível", variant: "destructive" });
      return;
    }
    if (unsupportedChannel) {
      toast({
        title: "Canal não suportado",
        description: "Envio de imagens disponível apenas em WhatsApp nesta versão.",
        variant: "destructive",
      });
      return;
    }

    const picked = images.filter((i) => selected.has(i.id));
    if (picked.length === 0 && !sendText && !sendAsCarousel) {
      toast({ title: "Nada selecionado", description: "Selecione ao menos uma imagem ou o texto.", variant: "destructive" });
      return;
    }

    if (sendAsCarousel && carouselAvailable && picked.length >= 1) {
      setSending(true);
      try {
        let resolvedUrls: Record<string, { url: string }> = {};
        const res = await supabase.functions.invoke("resolve-product-image-urls", {
          body: {
            workspace_id: workspaceId,
            product_id: productId,
            image_ids: picked.map((p) => p.id),
          },
        });
        if (res.error) {
          const msg = (res.data as any)?.message || res.error.message;
          toast({ title: "Erro ao preparar imagens", description: msg, variant: "destructive" });
          return;
        }
        const list = ((res.data as any)?.images || []) as Array<{ id: string; url: string }>;
        for (const r of list) resolvedUrls[r.id] = { url: r.url };

        const cardTextBase = [productName, productPriceLabel].filter(Boolean).join("\n");
        const fab_interactive = buildFabInteractiveFromProductCarousel({
          productName,
          bodyText: caption.trim() || text.trim() || productName,
          productUrl,
          cards: picked
            .map((img) => resolvedUrls[img.id]?.url)
            .filter(Boolean)
            .map((imageUrl) => ({
              imageUrl,
              text: cardTextBase || productName,
            })),
        });

        const { data, error } = await supabase.functions.invoke("whatsapp-uazapi-send-interactive", {
          body: {
            conversation_id: conversationId,
            fab_interactive,
          },
        });

        if (error || (data as any)?.error || (data as any)?.success === false) {
          toast({
            title: "Falha ao enviar carrossel",
            description:
              (data as any)?.friendly_message ||
              (data as any)?.error ||
              error?.message ||
              "Erro desconhecido",
            variant: "destructive",
          });
          return;
        }

        toast({ title: "Carrossel enviado" });
        onSent?.();
        onOpenChange(false);
      } finally {
        setSending(false);
      }
      return;
    }

    setSending(true);
    let sent = 0;
    let failed = 0;

    try {
      // 1) Resolve URLs (presign MinIO fresco)
      let resolvedUrls: Record<string, { url: string; mime_type: string; object_key?: string | null }> = {};
      if (picked.length > 0) {
        const res = await supabase.functions.invoke("resolve-product-image-urls", {
          body: {
            workspace_id: workspaceId,
            product_id: productId,
            image_ids: picked.map((p) => p.id),
          },
        });
        if (res.error) {
          const msg = (res.data as any)?.message || res.error.message;
          toast({ title: "Erro ao preparar imagens", description: msg, variant: "destructive" });
          setSending(false);
          return;
        }
        const list = ((res.data as any)?.images || []) as Array<{ id: string; url: string; mime_type: string; object_key?: string | null }>;
        for (const r of list) resolvedUrls[r.id] = { url: r.url, mime_type: r.mime_type, object_key: r.object_key };
      }

      // 2) Send each image sequentially with 400ms gap
      for (let i = 0; i < picked.length; i++) {
        const img = picked[i];
        const resolved = resolvedUrls[img.id];
        if (!resolved) {
          failed++;
          continue;
        }
        const { error: mediaErr } = await supabase.functions.invoke("send-media-message", {
          body: {
            conversationId,
            mediaType: "image",
            mediaUrl: resolved.url,
            objectKey: resolved.object_key || undefined,
            storageProvider: img.storage_provider === "minio" ? "minio" : undefined,
            mimetype: resolved.mime_type,
            caption: i === 0 && caption.trim() ? caption.trim() : undefined,
          },
        });
        if (mediaErr) {
          failed++;
          console.error("send-media-message error:", mediaErr);
        } else {
          sent++;
        }
        if (i < picked.length - 1) {
          await new Promise((r) => setTimeout(r, 400));
        }
      }

      // 3) Optional: send the textual summary
      if (sendText && text.trim()) {
        const { error: txtErr } = await supabase.functions.invoke("send-message", {
          body: { conversationId, content: text.trim() },
        });
        if (txtErr) {
          toast({ title: "Texto não enviado", description: txtErr.message, variant: "destructive" });
        }
      }

      toast({
        title: failed === 0 ? "Enviado" : "Envio parcial",
        description: `Imagens: ${sent} enviada(s)${failed ? `, ${failed} falha(s)` : ""}.`,
        variant: failed === 0 ? "default" : "destructive",
      });
      onSent?.();
      onOpenChange(false);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Enviar {productName}</DialogTitle>
          <DialogDescription>
            Selecione as imagens, ajuste a legenda e envie ao cliente.
          </DialogDescription>
        </DialogHeader>

        {unsupportedChannel && (
          <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-sm p-3">
            Envio de imagens disponível apenas no canal WhatsApp nesta versão.
          </div>
        )}

        <div className="space-y-4">
          <div>
            <p className="text-sm font-medium mb-2">Imagens ({images.length})</p>
            {loading ? (
              <div className="flex justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : images.length === 0 ? (
              <p className="text-sm text-muted-foreground">Este produto não tem imagens cadastradas.</p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {images.map((img) => {
                  const checked = selected.has(img.id);
                  return (
                    <button
                      key={img.id}
                      type="button"
                      onClick={() => toggle(img.id)}
                      className={cn(
                        "relative rounded-md overflow-hidden border bg-muted text-left",
                        checked ? "ring-2 ring-primary" : "opacity-70 hover:opacity-100",
                      )}
                    >
                      <img
                        src={productImageDisplaySrc({ ...img, isExternal: img.storage_provider === "external" }, displayUrls)}
                        alt=""
                        className="w-full aspect-square object-cover"
                        loading="lazy"
                      />
                      <span className="absolute top-1 left-1 bg-background/80 rounded px-1">
                        <Checkbox checked={checked} className="h-3 w-3" />
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {images.length > 0 && carouselAvailable && (
            <div className="rounded-md border bg-muted/30 p-3 space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="send-carousel"
                  checked={sendAsCarousel}
                  onCheckedChange={(v) => setSendAsCarousel(Boolean(v))}
                  disabled={selected.size < 1}
                />
                <label htmlFor="send-carousel" className="text-sm font-medium cursor-pointer">
                  Enviar como carrossel interativo
                </label>
              </div>
              <p className="text-xs text-muted-foreground">
                {selected.size >= 2
                  ? "Recomendado com 2 ou mais imagens — o cliente desliza entre os cartões no WhatsApp."
                  : "Selecione ao menos uma imagem para montar o carrossel."}
              </p>
            </div>
          )}

          {images.length > 0 && !sendAsCarousel && (
            <div>
              <label className="text-sm font-medium">Legenda (1ª imagem)</label>
              <Textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Ex.: Veja as fotos do produto que você pediu"
                rows={2}
                disabled={unsupportedChannel}
              />
            </div>
          )}

          <div>
            <div className="flex items-center gap-2 mb-1">
              <Checkbox
                id="send-text"
                checked={sendText}
                onCheckedChange={(v) => setSendText(Boolean(v))}
                disabled={sendAsCarousel}
              />
              <label htmlFor="send-text" className="text-sm font-medium cursor-pointer">
                {sendAsCarousel ? "Resumo usado no texto principal do carrossel" : "Enviar também o resumo em texto"}
              </label>
            </div>
            <Textarea
              value={sendAsCarousel ? caption || text : text}
              onChange={(e) => (sendAsCarousel ? setCaption(e.target.value) : setText(e.target.value))}
              rows={6}
              disabled={!sendText && !sendAsCarousel}
              placeholder={
                sendAsCarousel
                  ? "Texto principal do carrossel (opcional — usa o nome do produto se vazio)"
                  : undefined
              }
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
            Cancelar
          </Button>
          <Button onClick={() => void handleSend()} disabled={sending || unsupportedChannel}>
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Enviar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
